import { useGridRootProps } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, useCallback, useContext } from "react";
import { DataGridContext } from "../context/DataGridContext";
import { DataGridProps } from "../models/dataGridProps";

export const useResetGridState = (apiRef: MutableRefObject<GridApiPremium>) => {
  const gridDataContext = useContext(DataGridContext);
  const rootProps = useGridRootProps() as DataGridProps;

  const resetGridState = useCallback(() => {
    const initialState = rootProps.initialState || {};

    if (apiRef?.current) {
      const storageKey = `${rootProps.storageKey}-state`;
      localStorage.removeItem(storageKey);

      resetColumnsState(apiRef, rootProps);

      const curState = apiRef.current.exportState();
      const newState = {
        ...curState,
        columns: {
          ...curState.columns,
          columnVisibilityModel: initialState?.columns?.columnVisibilityModel ?? curState.columns?.columnVisibilityModel,
        },
        // headerFilters is a custom property we are using to set a default value for headerFilters
        headerFilters: (<any>initialState)?.headerFilters ?? (<any>curState).headerFilters,
        density: initialState?.density || curState.density,
      };

      apiRef.current.restoreState(newState);

      apiRef.current.updateColumns(rootProps.columns as any);
    }
  }, [apiRef, gridDataContext]);

  return resetGridState;
};

const resetColumnsState = (apiRef: MutableRefObject<GridApiPremium>, rootProps: DataGridProps) => {
  let newOrderedFields = rootProps.columns.map((column) => column.field);

  // WARNING: we are missing "__check__" and "__tree_data_group__" which are not part of the default column state
  // so they end up being added to the end of orderedFields which messes up everything!
  // We are using the existance of treeData as an indicator that we need to include these fields.
  if (rootProps.treeData) {
    newOrderedFields = [
      "__check__",
      "__tree_data_group__",
      ...newOrderedFields,
    ];
  }

  const curState = apiRef.current.state;
  const newState = {
    ...curState,
    columns: {
      ...curState.columns,
      dimensions: {},
      orderedFields: newOrderedFields
    }
  };

  apiRef.current.setState(newState);
};
